// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import CartPageTemplate, { CartPageTemplateStyles } from "@guestsupply/App/Components/Cart/CartPageTemplate";
import UpsellsModal from "@guestsupply/App/Components/Cart/UpsellsModal";
import XngZone from "@guestsupply/App/Components/Common/XngZone";
import getUpsellsProducts from "@guestsupply/App/Handlers/UpsellsProducts/getUpsellsProducts";
import { getSettingsCollection } from "@guestsupply/App/Store/Selectors/SettingsSelectors";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useContext, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";

const mapDispatchToProps = {
    getUpsellsProducts,
};
export interface CartPageContainerStyles {
    container?: InjectableCss;
    headerContainer?: InjectableCss;
    cartLinesContainer?: InjectableCss;
    fulfillmentMethodContainer?: InjectableCss;
    cartTotalContainer?: InjectableCss;
    productCarouselContainer?: InjectableCss;
    // XNG-Changes
    cartPageTemplateStyles?: CartPageTemplateStyles;
    cartTotalMobileWrapper?: InjectableCss;
    cartTotalWrapper?: InjectableCss;
}

export const cartPageContainerStyles: CartPageContainerStyles = {};

const styles = cartPageContainerStyles;

const CartPageContainer = ({ getUpsellsProducts, id }: WidgetProps & ResolveThunks<typeof mapDispatchToProps>) => {
    const [isLoading, setIsLoading] = useState(false);
    const [upsellsProducts, setUpsellsProducts] = useState<ProductModel[]>([]);
    const toasterContext = useContext(ToasterContext);

    // XNG-Changes
    const { cart, showUpsellsModal } = useAppSelector(state => {
        return {
            cart: getCurrentCartState(state).value,
            showUpsellsModal: getSettingsCollection(state).customSettings.showUpsellsModal,
        };
    });

    const { isInShell } = useShellContext();

    // call upsells API
    useEffect(() => {
        const canCallUpsellsApi =
            !upsellsProducts.length &&
            !isLoading &&
            !isInShell &&
            cart?.cartLines &&
            cart?.cartLines?.length > 0 &&
            showUpsellsModal;
        if (canCallUpsellsApi) {
            setIsLoading(true);
            getUpsellsProducts({
                onError: errorMessage => {
                    toasterContext.addToast({ body: errorMessage, messageType: "danger" });
                },
                onComplete: result => {
                    if (result.apiResult?.products) {
                        setUpsellsProducts(result.apiResult?.products);
                    }
                    setIsLoading(false);
                },
            });
        }
    }, [cart?.cartLines]);

    const isEmptyCart = !cart || cart?.cartLines?.length === 0;

    // XNG-Changes: conditional render
    return (
        <>
            <CartPageTemplate extendedStyles={styles.cartPageTemplateStyles}>
                <CartPageTemplate.MainSection>
                    <CartPageTemplate.HeaderSection>
                        <XngZone zoneName="Content0" contentId={id} hint="Cart - Title" />
                        <StyledWrapper {...styles.cartTotalMobileWrapper}>
                            <XngZone zoneName="Content2" contentId={id} hint="Cart - CartTotal" />
                        </StyledWrapper>
                    </CartPageTemplate.HeaderSection>
                    {!isEmptyCart && (
                        <CartPageTemplate.CartMessages>
                            <XngZone zoneName="Content3" contentId={id} hint="Cart - Cart Messages And Actions" />
                        </CartPageTemplate.CartMessages>
                    )}
                    <XngZone zoneName="Content1" contentId={id} hint="Cart - CartLines table" />
                </CartPageTemplate.MainSection>
                <CartPageTemplate.CartTotalSection>
                    <StyledWrapper {...styles.cartTotalWrapper}>
                        <XngZone zoneName="Content2" contentId={id} hint="Cart - CartTotal" />
                    </StyledWrapper>
                </CartPageTemplate.CartTotalSection>
            </CartPageTemplate>
            <UpsellsModal products={upsellsProducts} />
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(null, mapDispatchToProps)(CartPageContainer),
    definition: {
        group: "Cart",
        allowedContexts: ["CartPage"],
        displayName: "Page Container",
    },
};

export default widgetModule;
